import { DivWithSpinner, IntegratedDialogError } from "@tedivo/tedivo-ui";
import {
  IFields,
  IFormReturn,
  TedivoForm,
  translateTedivoForm,
} from "@tedivo/tedivo-form";
import {
  IFilesCount,
  IOrganization,
  IParentOrganization,
  TPlan,
} from "@tedivo/tvd-api-models";
import {
  IOptionsStorage,
  ITableOnClickOptions,
  SmartTable,
} from "@tedivo/tedivo-smart-table";
import Services, { AdminServices } from "../../../app/services";
import {
  addTimeZoneOffset,
  allTimezonesArray,
  createDictionary,
  formatDateInCurrentTimezone,
  pad,
} from "@tedivo/tedivo-pure-helpers";
import { getTranslation, i18nReactive } from "../../../app/i18/i18tn";

import { EditDrawer } from "../../common/EditDrawer";
import { I18nComponentRegisterer } from "@tedivo/tedivo-i18";
import ICustomError from "../../types/ICustomError";
import { IViewStateRedirectOrg } from "./parentOrganizations.component";
import { ORG_PLAN_OPTIONS } from "../../../app/enums/OrgPlanTypeEnum";
import SlButton from "@shoelace-style/shoelace/dist/components/button/button";
import SlDialog from "@shoelace-style/shoelace/dist/components/dialog/dialog";
import { createTableUserStorageInstance } from "../../../app/preferences/createTableUserStorageInstance";
import globalStore from "../../../app/stores/globalStore";
import goSquared from "../../../app/tracking/goSquared";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";
import { routeFns } from "../../../app/router/routes";
import router from "../../../app/router";
import securityModule from "../../../app/security/SecurityModule";
import { setAppTitle } from "../../../app/setAppTitle";
import topMessageElement from "../../layout/top-tools/getTopMessageElement";
import { z } from "zod";

export class TVDAdminOrgsComponent extends HTMLElement {
  public static observedAttributes = [];

  private i18nCR: I18nComponentRegisterer;
  private dialogError: IntegratedDialogError;
  private wrapper: DivWithSpinner;
  private mainBox: HTMLDivElement;
  private deleteDialog: SlDialog;
  private editDrawer: EditDrawer;

  private parentOrgs: Record<string, IParentOrganization> = {};

  constructor() {
    super();
    this.i18nCR = new I18nComponentRegisterer(i18nReactive);
    this.dialogError = new IntegratedDialogError(
      this,
      false,
      getTranslation("general:common.close"),
    );
    this.wrapper = document.createElement("div-spinner-element");
    this.mainBox = document.createElement("div");
    this.mainBox.className = "oss-card";
    this.deleteDialog = document.createElement("sl-dialog");
    this.editDrawer = new EditDrawer();

    setAppTitle(getTranslation("general:organizations.organizations"));
  }

  connectedCallback() {
    goSquared.trackPage("TEDIVO Admin Pages - Organizations");
    goSquared.addEvent("Admin/Organizations - Show page");

    const wrapper = this.wrapper,
      h1 = document.createElement("h1");

    wrapper.appendChild(this.mainBox);

    this.appendChild(this.editDrawer.drawer);
    this.appendChild(h1);
    this.appendChild(wrapper);

    this.displayOrganizations();

    this.i18nCR.addConsumer(
      topMessageElement.element,
      "general:adminArea",
      "innerHTML",
    );

    this.i18nCR.addConsumer(
      h1,
      "general:organizations.organizations",
      "innerHTML",
    );
  }

  private displayOrganizations = () => {
    globalStore.touchLastUserInteraction();

    const wrapper = this.wrapper;
    const box = this.mainBox;
    removeChildren(box);

    const actionAddOrgButton = document.createElement("sl-button");
    actionAddOrgButton.innerHTML = getTranslation(
      "general:organizations.adminCreate",
    );
    actionAddOrgButton.variant = "primary";
    actionAddOrgButton.addEventListener(
      "click",
      () => this.openOrganizationEditModal(undefined),
      false,
    );

    wrapper.withLoading(async () => {
      try {
        const [resp, parOrgsResp] = await Promise.all([
          Services.organizations.adminGetAllOrganizations(),
          AdminServices.parentOrganizations.adminGetAllParentOrgs(),
        ]);

        if (parOrgsResp.data) {
          this.parentOrgs = createDictionary(
            parOrgsResp.data,
            (d) => d.parentOrganizationId,
          );
        }

        if (resp.data) {
          const userOptionsStorage =
            createTableUserStorageInstance<IOrganization>("adminOrgsList");

          box.appendChild(
            createOrgsTable({
              organizations: resp.data,
              userOptionsStorage,
              actionBox: actionAddOrgButton,
              onFilterTextChange: (filterText) => {
                globalStore.lastTextFilterInAdminOrgs = filterText;
              },
              onEditOrganization: (org: IOrganization) => {
                this.openOrganizationEditModal({ ...org });
              },
              onDeleteOrganization: this.deleteOrganization,
              parentOrgs: this.parentOrgs,
            }),
          );

          const editOrgId = (router.currentState as IViewStateRedirectOrg)
            ?.organizationId;

          if (editOrgId) {
            const org = resp.data.find(
              (f) => f.organizationId === editOrgId,
            ) as IOrganization;

            if (org) {
              this.openOrganizationEditModal(org);
            }
          }
        }
      } catch (e) {
        this.showError(e as ICustomError);
      }
    });
  };

  disconnectedCallback() {
    this.i18nCR.disconnect();
  }

  showError = (e: ICustomError) => {
    this.dialogError.show(
      getTranslation(`errors:errorHasOcurred`),
      getTranslation(typeof e === "string" ? e : e.translationKey),
    );
    console.error(e);
  };

  private openOrganizationEditModal = async (
    prevData: IOrganization | undefined,
  ) => {
    const editForm = createOrgEdit(
      this.editDrawer.submitButton,
      prevData,
      Object.values(this.parentOrgs),
    );

    const returnUrl = (router.currentState as IViewStateRedirectOrg)?.returnUrl;

    if (returnUrl) router.clearHistoryViewState();

    if (editForm) {
      const drawer = this.editDrawer.getEditDrawer({
        title: prevData
          ? getTranslation("general:organizations.adminEdit")
          : getTranslation("general:organizations.adminCreate"),
        showUnits: false,
        readOnlyMode: false,
        onCancel: () => {
          if (returnUrl) {
            router.navigate(returnUrl);
          }
        },
        onSave: async () => {
          this.editDrawer.disabled = true;
          const valid = await editForm.submitFunction();
          this.editDrawer.disabled = false;

          if (valid) {
            if (returnUrl) {
              router.navigate(returnUrl);
            } else {
              this.displayOrganizations();
            }
          }

          return valid;
        },
      });
      drawer.appendChild(editForm.node);
      drawer.show();
    }
  };

  private deleteOrganization = (id: string, dta: IOrganization) => {
    if (id === securityModule.currentOrganizationId) return;

    const dialog = this.deleteDialog;
    removeChildren(dialog);

    dialog.label = getTranslation("general:organizations.delete");

    const htmlContent = document.createElement("div");
    htmlContent.innerHTML = `${getTranslation(
      "general:organizations.delete",
    )}<br /><br /><strong>${dta.name}</strong>`;

    const closeBtn = document.createElement("sl-button");
    closeBtn.slot = "footer";
    closeBtn.variant = "danger";
    closeBtn.autofocus = true;
    closeBtn.tabIndex = 0;
    closeBtn.innerHTML = getTranslation("general:common.delete");
    closeBtn.addEventListener(
      "click",
      async () => {
        closeBtn.disabled = true;
        await Services.organizations.adminDeleteOrganization(id);
        dialog.hide();
        this.displayOrganizations();
      },
      false,
    );

    dialog.appendChild(htmlContent);
    dialog.appendChild(closeBtn);
    this.appendChild(dialog);

    dialog.show();
  };
}

customElements.define("tvd-admin-orgs-component", TVDAdminOrgsComponent);

declare global {
  interface HTMLElementTagNameMap {
    "tvd-admin-orgs-component": TVDAdminOrgsComponent;
  }
}

function createOrgsTable({
  organizations = [],
  userOptionsStorage,
  actionBox,
  onFilterTextChange,
  onEditOrganization,
  onDeleteOrganization,
  parentOrgs,
}: {
  organizations: IOrganization[];
  userOptionsStorage: IOptionsStorage<IOrganization>;
  actionBox: HTMLElement | undefined;
  onFilterTextChange: (filterText: string) => void;
  onEditOrganization: (org: IOrganization, btn?: SlButton) => void;
  onDeleteOrganization: (id: string, org: IOrganization) => void;
  parentOrgs: Record<string, IParentOrganization>;
}): HTMLElement {
  const createSortIcon = (asc: boolean) => {
    const icon = document.createElement("sl-icon");
    icon.name = asc ? "sort-down-alt" : "sort-up-alt";
    return icon;
  };

  const tableUsers = new SmartTable<IOrganization>().initialize({
    className: "tvd-table tvd-table-smart-table",
    data: organizations,
    defaultSortBy: "name",
    fields: [
      {
        name: "name",
        label: getTranslation("general:common.name"),
        mapper: (org) => {
          if (org.parentOrganizationId) {
            const parentOrg = parentOrgs[org.parentOrganizationId];
            return `${org.name} <small>(${parentOrg?.name})</small>`;
          }
          return org.name;
        },
      },
      {
        name: "planId",
        label: getTranslation("general:organizations.plan"),
        mapper: (org) => getTranslation(`enums:OrgPlanTypeEnum.${org.planId}`),
      },
      {
        name: "enabled",
        label: getTranslation("general:common.status"),
        mapper: (org) =>
          getTranslation(
            `general:common.${org.enabled ? "enabled" : "disabled"}`,
          ),
      },
      {
        name: "usersCount",
        label: getTranslation("general:users.users"),
        className: "centered",
        mapper: (org) => {
          const btnLink = document.createElement("link-button-element");
          btnLink.link = routeFns.adminOrganizationUsers(org.organizationId);
          btnLink.innerHTML = String(org.usersCount || 0);
          btnLink.onClick = router.navigate;
          return btnLink;
        },
      },
      {
        name: "createdAt",
        label: getTranslation("general:common.createdAt"),
        valueFunction: (dta) => formatDateInCurrentTimezone(dta.createdAt),
        className: "centered",
      },
      {
        name: "modifiedAt",
        label: getTranslation("general:common.modifiedAt"),
        valueFunction: (dta) => formatDateInCurrentTimezone(dta.modifiedAt),
        className: "centered",
      },
      {
        name: "expiresAt",
        label: getTranslation("general:organizations.expiresAtUtc"),
        valueFunction: (dta) =>
          formatDateInCurrentTimezone(dta?.expiresAt, {
            omitTime: false,
            timezone: "UTC",
          }),
        className: "centered",
      },
      {
        name: "filesCount",
        label: getTranslation("general:common.vesselFiles"),
        className: "centered",
        sortValueFunction: (dta) =>
          pad(
            (dta.filesCount?.draft || 0) +
              (dta.filesCount?.private || 0) +
              (dta.filesCount?.community || 0),
            10,
          ),
        mapper: (org) => {
          const btnLink = document.createElement("link-button-element");
          btnLink.onClick = router.navigate;
          btnLink.state = { organizationName: org.name };
          btnLink.link = routeFns.theirCloud(org.organizationId);
          btnLink.innerHTML = `${org.filesCount?.draft || "-"}/${
            org.filesCount?.private || "-"
          }/${org.filesCount?.community || "-"}`;

          btnLink.buttonElement.variant = "success";
          btnLink.buttonElement.title = createAltTextForFilesState(
            org.filesCount || {},
          );

          return btnLink;
        },
      },
      {
        name: "organizationId",
        label: getTranslation("general:common.actions"),
        className: "align-left",
        iconVariant: "primary",
        type: "actions",
        icon: "three-dots-vertical",
        notSortable: true,
        fixedWidth: "60px",
        smallPadding: true,
        options: (dta) =>
          [
            {
              label: getTranslation("general:organizations.adminEdit"),
              value: "orgEdit",
              icon: "pencil",
            },
            {
              label: getTranslation("general:organizations.invoices.title"),
              value: "orgInvoices",
              icon: "receipt-cutoff",
            },
            {
              label: getTranslation("general:users.users"),
              value: "orgUsers",
              icon: "people",
            },
            dta.organizationId !== securityModule.currentOrganizationId
              ? {
                  label: getTranslation("general:common.delete"),
                  value: "orgDelete",
                  icon: "trash",
                  variant: "danger",
                }
              : undefined,
          ].filter(Boolean) as ITableOnClickOptions[],
        onClick: (dta, name, table, value) => {
          switch (value) {
            case "orgEdit":
              onEditOrganization(dta, undefined);
              break;
            case "orgDelete":
              onDeleteOrganization(dta.organizationId, dta);
              break;
            case "orgInvoices":
              router.navigate(routeFns.adminInvoices(dta.organizationId), {
                state: {
                  organizationName: dta.name,
                },
              });
              break;
            case "orgUsers":
              router.navigate(
                routeFns.adminOrganizationUsers(dta.organizationId),
                {
                  state: {
                    organizationName: dta.name,
                  },
                },
              );
              break;
            default:
              console.warn("Unknown action", value);
              break;
          }
          //TODO
        },
      },
    ],
    settings: {
      sortEnabled: true,
      sortVisible: true,
      userOptionsStorage,
      filters: {
        textFieldEnabled: true,
        textFieldFilterFields: ["name"],
        initialTextFieldFilter: userOptionsStorage.retrieveFilterText(),
      },
      labelSortBy: getTranslation("general:common.sortBy"),
      labelSearch: getTranslation("general:common.filterBy"),
      createSortIconFn: createSortIcon,
      actionBox,
    },
    onFilterTextChange,
    pkFunction: (dta) => dta.organizationId,
  });

  return tableUsers;
}

function createFormValidator(): z.Schema<IOrganizationEdit> {
  const formValidator = z.object({
    name: z.string().min(1),
    enabled: z.boolean(),
    planId: z.string(),
    domains: z.string(),
    freeTrialMax: z.number().min(1),
  });

  return formValidator as unknown as z.Schema<IOrganizationEdit>;
}

function createOrgEdit(
  submitButton: SlButton,
  prevData: IOrganization | undefined,
  parentOrgs: IParentOrganization[] | undefined,
): IFormReturn<IOrganizationEdit> | null {
  const holder = document.createElement("div");

  const offsettedExpiresAt = prevData?.expiresAt
    ? addTimeZoneOffset(
        new Date(prevData.expiresAt),
        prevData.timezone || "America/Los_Angeles",
      )
    : undefined;

  const formFields: IFields<IOrganizationEdit> = [
    {
      name: "name",
      label: "general:common.name",
      type: "textBox",
      initialValue: prevData?.name || "",
    },
    {
      name: "planId",
      label: "general:organizations.plan",
      type: "radioButtonList",
      fieldset: true,
      options: ORG_PLAN_OPTIONS.map((f) => ({
        value: f.value,
        label: getTranslation(f.translationLabel),
      })),
      initialValue: prevData?.planId || "FREE",
    },
    {
      name: "freeTrialMax",
      label: "general:organizations.freeTrialMax",
      type: "number",
      initialValue: prevData?.freeTrialMax ?? 3,
      helpText: "Minimum 1",
    },
    {
      name: "domains",
      label: "general:organizations.domains",
      type: "textArea",
      helpText: "general:organizations.domainsHelp",
      initialValue: prevData?.domains?.join("\n") || "",
    },
    {
      name: "parentOrganizationId",
      label: "general:parentOrganizations.singular",
      type: "select",
      options: [
        {
          value: "",
          label: `- ${getTranslation(
            "general:parentOrganizations.noParent",
          )} -`,
        },
      ].concat(
        (parentOrgs || []).map((f) => ({
          value: f.parentOrganizationId,
          label: f.name,
        })),
      ),
      initialValue: prevData?.parentOrganizationId || "",
    },
    [
      {
        name: "expirationDate",
        type: "date",
        label: "general:common.expirationDate",
        initialValue: offsettedExpiresAt,
      },
      {
        name: "timezone",
        type: "select",
        options: allTimezonesArray.map((f) => ({
          value: f,
          label: f,
        })),
        label: "general:common.timezone",
        initialValue: prevData?.timezone || "America/Los_Angeles",
      },
      {
        name: "enabled",
        label: "general:common.enabled",
        type: "checkbox",
        initialValue: prevData?.enabled ?? true,
        padStart: true,
      },
    ],
  ];

  const tedivoForm = new TedivoForm<IOrganizationEdit>({
    fields: formFields,
    onSubmit: () => undefined,
    formValidator: createFormValidator(),
    submitButton: submitButton,
    formProps: { autoFocusOnFirstInput: true },
    hiddenData: {
      ...prevData,
      domains: prevData?.domains.join(",") || "",
    },
  });

  translateTedivoForm<IOrganizationEdit>({
    tedivoForm,
    getTranslation: getTranslation,
  });

  holder.appendChild(tedivoForm.form);

  return {
    node: holder,
    tedivoForm,
    submitFunction: submitPassedToEditDrawer,
  };

  async function submitPassedToEditDrawer() {
    const validResult = tedivoForm.doSubmitForm();

    if (validResult.success) {
      const values = tedivoForm.getValues();

      const { domains: domainsStr, expirationDate, ...rest } = values;
      const domains = (domainsStr || "")
        .split("\n")
        .map((f) =>
          f.trim().toLowerCase().replace(/^\./, "").replace(/\.$/, ""),
        )
        .filter(Boolean);

      if (expirationDate && rest.timezone) {
        expirationDate.setHours(23, 59, 59, 0);

        const offsetted = addTimeZoneOffset(
          expirationDate,
          rest.timezone,
          true,
        );

        expirationDate.setTime(offsetted.getTime());
      }

      if (prevData?.organizationId) {
        await Services.organizations.adminUpdateOrganization({
          ...prevData,
          ...rest,
          organizationId: prevData.organizationId,
          expiresAt: expirationDate,
          domains,
        });
      } else {
        const updRecord: IOrganization = {
          ...(rest as unknown as IOrganization),
          domains,
          expiresAt: expirationDate,
        };

        await Services.organizations.adminUpdateOrganization(updRecord);
      }
      return true;
    } else {
      return false;
    }
  }
}

interface IOrganizationEdit extends Record<string, unknown> {
  name: string;
  organizationId: string;
  planId: TPlan;
  enabled: boolean;
  domains: string;
  expirationDate: Date;
  timezone: string;
}

function createAltTextForFilesState(filesCount: Partial<IFilesCount>) {
  return `${getTranslation("enums:FileStateEnum.DRAFT")}: ${
    filesCount?.draft || "0"
  }
${getTranslation("enums:FileStateEnum.ORG_READY")}: ${
    filesCount?.private || "0"
  }
${getTranslation("enums:FileStateEnum.COMMUNITY")}: ${
    filesCount?.community || "0"
  }`;
}
